import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Keyboard, KeyboardAvoidingView, Platform, Pressable, ScrollView, Text, TextInput, useWindowDimensions, View } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIsFocused, useRouter, useSegments } from 'expo-router';
import Animated, { Easing, FadeIn, FadeOut, ReduceMotion, SlideInRight, SlideOutRight, useAnimatedStyle, useSharedValue, withRepeat, withSequence, withTiming } from 'react-native-reanimated';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import CutCard from '@/components/General/CutCard';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import PersonProfileModal from '@/components/People/PersonProfileModal';
import MarkdownText from '@/components/General/MarkdownText';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { deleteAssistantSession, getAssistantMessages, revertAssistantFromMessage, streamAssistantMessage } from '@/data/assistant';
import { ConfirmationModal, showAlert } from '@/utils/showAlert';
import usePlatform from '@/utils/hooks/usePlatform';
import useModalPortalStore, { ModalPortalContext } from '@/data/store/useModalPortalStore';

// const EXAMPLES = [
//     'How many people attended Sunday\'s service?',
//     'When was John Smith baptized?',
//     'What are Jane Smith\'s kids\' names?',
//     'Who has permission to Edit Events?',
// ];

const EXAMPLES = [
    'Who was the last person that got baptized?',
    'What are Jane Smith\'s kids\' names?',
    'What permission do I need to create volunteering tasks?',
    'How do I merge two people?'
];

const getChoices = (message) => message.structuredContent?.personChoices || message.personChoices || message.refusalMetadata?.personChoices || [];

// Fallback phrases cycled while the assistant streams but has produced no
// content or tool status yet.
const currentHour = new Date().getHours();
const EARLY_PHRASES = [
    'Walking downstairs to find the database...',
    'Okay here we go...',
    'Alright, let me check this out...',
    'Let me get my glasses real quick...'
];

const MID_PHRASES = [
    'Grabbing a coffee...',
    'Searching through the records...',
    'Fumbling through the filing cabinets...',
    'Doing AI things agentically with models...'
];

const LATE_PHRASES = [
    (currentHour <= 6 || currentHour >= 22) ? 'What are you doing up at this hour anyway?' : currentHour > 15 ? "I usually go home at 5..." : 'Figuring some things out here really quick...',
    "There's a lot of records...",
    'Assembling the results...',
    "I'm almost done I promise... Maybe..."
];

// Picks 2 random phrases from each phase pool so the rotation roughly
// tracks progress (early → mid → late).
const pickPhrases = () => {
    const pickTwo = (pool) => [...pool].sort(() => Math.random() - 0.5).slice(0, 2);
    return [...pickTwo(EARLY_PHRASES), ...pickTwo(MID_PHRASES), ...pickTwo(LATE_PHRASES)];
};

// Edge function tool names → friendly status text.
const TOOL_ACTIVITY = {
    search_people: 'Searching people…',
    search_people_by_field: 'Searching records…',
    get_person_profile: 'Reading profile…',
    list_custom_fields: 'Checking fields…',
    count_people: 'Counting records…',
    count_people_by_address: 'Counting by address…',
    search_permission_requirements: 'Checking permissions…',
    check_my_access: 'Checking your access…',
    check_person_access: 'Checking permissions…',
    find_resource: 'Finding that item…',
    search_docs: 'Searching documentation…',
    read_doc_section: 'Reading documentation…',
};

const SessionCard = ({ session, selected = false, busy, onSelect, onDelete, testID, style }) => (
    <CutCard
        testID={testID}
        accessibilityLabel={`Open ${session.title || 'Untitled session'}`}
        disabled={busy}
        onPress={() => onSelect(session.id)}
        accentColor={selected ? 'tertiary' : undefined}
        style={[styles.sessionCard(selected), style]}
    >
        <View style={styles.sessionRow}>
            <View style={styles.sessionSelect}>
                <View style={styles.sessionIcon(selected)}>
                    <Unicon name="chatbubble-outline" size={18} colorVariant={selected ? 'tertiary' : 'textSecondary'} />
                </View>
                <View style={styles.sessionDetails}>
                    <Text style={styles.sessionRowTitle} numberOfLines={2}>{session.title || 'Untitled session'}</Text>
                    <Text style={styles.sessionDate}>{new Date(session.updatedAt || session.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Text>
                </View>
                {!onDelete && <Unicon name="arrow-forward" size={16} colorVariant="textTertiary" />}
            </View>
            {!!onDelete && (
                <Pressable
                    testID={`assistant-session-delete-${session.id}`}
                    accessibilityRole="button"
                    accessibilityLabel={`Delete ${session.title || 'session'}`}
                    disabled={busy}
                    style={styles.deleteButton}
                    onPress={() => onDelete(session)}
                >
                    <Unicon name="trash-outline" size={18} colorVariant="textTertiary" />
                </Pressable>
            )}
        </View>
    </CutCard>
);

const SessionDrawer = ({ isOpen, onClose, sessions, selectedSessionID, loading, error, busy, onSelect, onDelete }) => (
    <SheetModal
        isOpen={isOpen}
        onClose={onClose}
        title="Past sessions"
        subtitle="Pick up where you left off."
        cancelText="Close"
        width={420}
    >
        {loading ? (
            <View style={styles.center}><ActivityIndicator /></View>
        ) : (
            <View testID="assistant-sessions-drawer" style={styles.sessionList}>
                {!!error && <Text testID="assistant-sessions-error" selectable style={styles.emptySessions}>Could not load past sessions.</Text>}
                {!error && !sessions?.length && <Text selectable style={styles.emptySessions}>No past sessions yet.</Text>}
                {sessions?.map((session) => (
                    <SessionCard
                        key={session.id}
                        testID={`assistant-session-${session.id}`}
                        session={session}
                        selected={session.id === selectedSessionID}
                        busy={busy}
                        onSelect={onSelect}
                        onDelete={onDelete}
                    />
                ))}
            </View>
        )}
    </SheetModal>
);

const PersonChoices = ({ choices, onSelect, disabled, messageID }) => {
    if (!choices?.length) return null;
    return (
        <View style={styles.choiceList}>
            <Text style={styles.choiceLabel}>Choose a person</Text>
            {choices.map((choice) => (
                <Pressable
                    key={choice.personID}
                    testID={`assistant-person-choice-${messageID}-${choice.personID}`}
                    accessibilityRole="button"
                    disabled={disabled}
                    style={styles.choiceCard(disabled)}
                    onPress={() => onSelect(choice)}
                >
                    <Avatar size={36} disableBorder onPress={() => onSelect(choice)} profile={{ id: choice.personID, firstName: choice.displayName?.split(' ')[0], lastName: choice.displayName?.split(' ').slice(1).join(' '), avatarStorageKey: choice.avatarStorageKey ?? null, avatarUpdatedAt: choice.avatarUpdatedAt }} />
                    <View style={styles.choiceText}>
                        <Text style={styles.choiceName}>{choice.displayName}</Text>
                        {!!choice.disambiguator && <Text selectable style={styles.choiceDisambiguator}>{choice.disambiguator}</Text>}
                    </View>
                    <Unicon name="chevron-forward" size={17} colorVariant="textTertiary" />
                </Pressable>
            ))}
        </View>
    );
};

const SOURCE_ICONS = { person: 'person-outline', doc: 'book-outline' };

// Remembers the last-opened assistant session per org+user for the lifetime
// of the app process — closing and reopening the assistant restores it, but
// a page refresh or app restart starts fresh (module state is not persisted).
const lastSessionByContext = new Map();
const sessionContextKey = (organizationID, personID) => `${organizationID ?? ''}:${personID ?? ''}`;

const TrustedSources = ({ sources, onOpen, messageID }) => {
    const visibleSources = sources?.filter((source) => SOURCE_ICONS[source.type] && source.recordID) || [];
    if (!visibleSources.length) return null;
    return (
        <View style={styles.sources}>
            {visibleSources.map((source) => (
                <Pressable
                    key={`${source.type}-${source.recordID}-${source.label}`}
                    testID={`assistant-source-${messageID}-${source.recordID}`}
                    accessibilityRole="button"
                    style={styles.sourceChip}
                    onPress={() => onOpen(source)}
                >
                    <Unicon name={SOURCE_ICONS[source.type]} size={13} colorVariant="tertiary" />
                    <Text style={styles.sourceText} numberOfLines={1}>{source.label}</Text>
                </Pressable>
            ))}
        </View>
    );
};

// Live status line inside a streaming-but-empty assistant bubble.
// Tool status text wins; otherwise rotates through the picked phrases.
const StreamingStatus = ({ activity }) => {
    const activityText = TOOL_ACTIVITY[activity];
    const [index, setIndex] = useState(0);
    const [phrases] = useState(pickPhrases);
    const shimmer = useSharedValue(1);
    useEffect(() => {
        shimmer.value = withRepeat(
            withSequence(
                withTiming(0.35, { duration: 800, easing: Easing.inOut(Easing.ease), reduceMotion: ReduceMotion.System }),
                withTiming(1, { duration: 800, easing: Easing.inOut(Easing.ease), reduceMotion: ReduceMotion.System }),
            ),
            -1,
            true,
            undefined,
            ReduceMotion.System,
        );
    }, [shimmer]);
    useEffect(() => {
        if (activityText) return;
        const interval = setInterval(() => setIndex((current) => (current + 1) % phrases.length), 4000);
        return () => clearInterval(interval);
    }, [activityText, phrases.length]);
    const shimmerStyle = useAnimatedStyle(() => ({ opacity: shimmer.value }));
    const text = activityText || phrases[index];
    return (
        <Animated.Text key={text} testID="assistant-status-text" entering={FadeIn.duration(220)} style={[styles.statusLine, shimmerStyle]}>
            {text}
        </Animated.Text>
    );
};

const MessageBubble = ({ message, streaming, disabled, onRevert, onRetry, onPersonSelect, onOpenSource }) => {
    const isUser = message.role === 'user';
    const failed = ['failed', 'interrupted'].includes(message.status);
    // Server redacts assistant answers the viewer lost access to: content
    // arrives '' and structuredContent carries the accessRestricted flag.
    const restricted = !isUser && message.structuredContent?.accessRestricted === true;
    return (
        <View testID={`assistant-message-${message.id}`} style={styles.messageRow(isUser)}>
            <View style={styles.messageColumn(isUser)}>
                <View style={styles.bubble(isUser)}>
                    {isUser ? (
                        <Text selectable style={styles.messageText(isUser)}>{message.content || message.error || ''}</Text>
                    ) : restricted ? (
                        <Text testID={`assistant-restricted-${message.id}`} selectable style={styles.statusLine}>This answer references something you no longer have access to.</Text>
                    ) : !message.content && streaming ? (
                        <StreamingStatus activity={message.activity} />
                    ) : (
                        <MarkdownText source={message.content || message.error || ''} style={styles.messageText(isUser)} />
                    )}
                    {streaming && !!message.content && <View style={styles.cursor} />}
                </View>
                {!isUser && <PersonChoices messageID={message.id} choices={getChoices(message)} onSelect={onPersonSelect} disabled={disabled} />}
                {!isUser && !getChoices(message)?.length && <TrustedSources messageID={message.id} sources={message.trustedSources} onOpen={onOpenSource} />}
                <View style={styles.messageActions(isUser)}>
                    {isUser && !message.pending && (
                        <Pressable testID={`assistant-revert-${message.id}`} accessibilityRole="button" accessibilityLabel="Revert from this message" disabled={disabled} onPress={() => onRevert(message)}>
                            <Text style={styles.actionText(disabled)}>Revert</Text>
                        </Pressable>
                    )}
                    {!isUser && failed && !streaming && (
                        <Pressable testID={`assistant-retry-${message.id}`} accessibilityRole="button" accessibilityLabel="Retry response" disabled={disabled} onPress={() => onRetry(message)}>
                            <Text style={styles.actionText(disabled)}>Retry</Text>
                        </Pressable>
                    )}
                    {!isUser && failed && <Text style={styles.statusText}>{message.status === 'interrupted' ? 'Stopped' : 'Failed'}</Text>}
                </View>
            </View>
        </View>
    );
};

const AssistantScreen = () => {
    const router = useRouter();
    const segments = useSegments();
    const queryClient = useQueryClient();
    const { isWeb } = usePlatform();
    const { theme: { themeName } } = useUnistyles();
    const isFocused = useIsFocused();
    const { width } = useWindowDimensions();
    const [sessionID, setSessionID] = useState(null);
    const [composer, setComposer] = useState('');
    const [transientMessages, setTransientMessages] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [sessionsOpen, setSessionsOpen] = useState(false);
    // { personID, displayName, allowSend } — allowSend adds the
    // "Send to Assistant" confirm action (person disambiguation picks only).
    const [profileModal, setProfileModal] = useState(null);
    const [isClosing, setIsClosing] = useState(false);
    const abortRef = useRef(null);
    const scrollRef = useRef(null);
    const transientCounter = useRef(0);
    const modalNodeRef = useRef(null);

    // Register modal DOM node as a Radix Portal container for DropdownMenu
    // content on web — same pattern as ModalWrapper/SheetModal.
    const registerModalNode = useCallback((node) => {
        if (!isWeb) return;
        if (node) {
            modalNodeRef.current = node;
            useModalPortalStore.getState().register(node);
        } else {
            const prev = modalNodeRef.current;
            if (prev) useModalPortalStore.getState().unregister(prev);
            modalNodeRef.current = null;
        }
    }, [isWeb]);

    useEffect(() => () => {
        if (isWeb && modalNodeRef.current) useModalPortalStore.getState().unregister(modalNodeRef.current);
    }, [isWeb]);

    // Portal host for this screen's subtree (DropdownMenu/Zeego popovers on
    // web) — same pattern as ModalWrapper/SheetModal. The outer container is
    // provided, not the registered panel: it never carries a transform, so
    // position:fixed popovers keep viewport coordinates.
    const [portalHost, setPortalHost] = useState(null);
    const registerPortalHost = useCallback((node) => {
        if (isWeb) setPortalHost(node);
    }, [isWeb]);

    const close = useCallback(() => {
        if (isWeb) {
            setIsClosing(true);
            setTimeout(() => {
                Keyboard.dismiss();
                router.back();
                setIsClosing(false);
            }, 300);
        } else {
            Keyboard.dismiss();
            router.back();
        }
    }, [isWeb, router]);

    // Escape key closes the modal on web — same pattern as ModalWrapper.
    useEffect(() => {
        if (!isWeb) return;
        const listener = (e) => {
            if (!isFocused || sessionsOpen || profileModal) return;
            if (e.key === 'Escape') {
                e.preventDefault();
                close();
            }
        };
        window.addEventListener('keydown', listener, true);
        return () => window.removeEventListener('keydown', listener, true);
    }, [isWeb, isFocused, sessionsOpen, profileModal, close]);

    const { data: loggedInUser, isPending: userLoading } = Query.people.loggedInUser();
    const hasPermission = loggedInUser?.can?.('Use AI Assistant');
    const organizationID = loggedInUser?.organizationID;
    const personID = loggedInUser?.id;
    const { data: sessions, isPending: sessionsLoading, error: sessionsError } = Query.assistant.sessions({
        organizationID,
        personID,
        options: { enabled: !!hasPermission && !!organizationID && !!personID },
    });
    // Restore the last-opened session for this org+user on remount; an
    // explicit "New session" or deleting the session clears the memory.
    useEffect(() => {
        if (!organizationID || !personID) return;
        const key = sessionContextKey(organizationID, personID);
        if (sessionID) {
            lastSessionByContext.set(key, sessionID);
        } else {
            const last = lastSessionByContext.get(key);
            if (last) setSessionID(last);
        }
    }, [sessionID, organizationID, personID]);

    const { data: persistedMessages, isFetching: messagesFetching } = Query.assistant.messages({
        organizationID,
        personID,
        sessionID,
        options: { enabled: !!sessionID && !!hasPermission && !!organizationID && !!personID && !isStreaming },
    });

    const messages = useMemo(() => {
        const byID = new Map();
        // Transient first so persisted (with server id) overrides for the same id.
        [...transientMessages, ...(persistedMessages || [])].forEach((message) => byID.set(message.id, message));
        return [...byID.values()].sort((a, b) => {
            if (a.sequence != null && b.sequence != null) {
                if (a.sequence !== b.sequence) return a.sequence - b.sequence;
            } else if (a.sequence != null) {
                return -1;
            } else if (b.sequence != null) {
                return 1;
            }
            // Tie-breaker: createdAt/client timestamp ascending.
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : a.clientTimestamp ?? 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : b.clientTimestamp ?? 0;
            return aTime - bTime;
        });
    }, [persistedMessages, transientMessages]);

    useEffect(() => () => abortRef.current?.abort(), []);
    useEffect(() => {
        if (!messages.length) return;
        requestAnimationFrame(() => scrollRef.current?.scrollToEnd?.({ animated: true }));
    }, [messages.length, transientMessages.at(-1)?.content]);

    const refresh = useCallback(async (activeSessionID = sessionID) => {
        let refreshedMessages = [];
        if (activeSessionID && organizationID && personID) {
            refreshedMessages = await queryClient.fetchQuery({
                queryKey: QueryKeys.assistant.messages(organizationID, personID, activeSessionID),
                queryFn: () => getAssistantMessages({ organizationID, sessionID: activeSessionID }),
                staleTime: 0,
            });
        }
        await queryClient.invalidateQueries({ queryKey: QueryKeys.assistant.sessions(organizationID, personID) });
        return refreshedMessages;
    }, [organizationID, personID, queryClient, sessionID]);

    const send = useCallback(async (contentOverride) => {
        // Accept either a string content or { selection } object.
        const isSelection = contentOverride && typeof contentOverride === 'object' && contentOverride.selection;
        const selection = isSelection ? contentOverride.selection : null;
        const content = isSelection ? (selection.displayName || '').trim() : (contentOverride ?? composer).trim();
        if (!content || isStreaming || !hasPermission) return;
        if (selection && !sessionID) return; // Person choices only come from an existing session.
        const now = Date.now();
        const localUserID = `pending-user-${now}-${transientCounter.current++}`;
        const localAssistantID = `pending-assistant-${now}-${transientCounter.current++}`;
        const userClientKey = localUserID;
        const assistantClientKey = localAssistantID;
        let activeSessionID = sessionID;
        let serverUserID = null;
        let serverAssistantID = null;
        let acknowledged = false;
        let streamError = null;
        const controller = new AbortController();
        abortRef.current = controller;
        setComposer('');
        setIsStreaming(true);
        setTransientMessages([
            { id: localUserID, clientKey: userClientKey, clientTimestamp: now, role: 'user', content, status: 'pending', pending: true, ...(selection ? { structuredContent: { selection } } : {}) },
            { id: localAssistantID, clientKey: assistantClientKey, clientTimestamp: now, role: 'assistant', content: '', status: 'streaming', pending: true },
        ]);

        const updateAssistant = (updates) => setTransientMessages((current) => current.map((message) => message.role === 'assistant' ? { ...message, ...updates } : message));
        try {
            await streamAssistantMessage({
                sessionID: activeSessionID,
                content,
                selection,
                signal: controller.signal,
                onEvent: ({ type, data }) => {
                    if (type === 'message') {
                        acknowledged = true;
                        activeSessionID = data.sessionID || activeSessionID;
                        serverUserID = data.userMessageID || serverUserID;
                        serverAssistantID = data.assistantMessageID || serverAssistantID;
                        if (data.sessionID) setSessionID(data.sessionID);
                        setTransientMessages((current) => current.map((message) => {
                            if (message.role === 'user') return { ...message, id: serverUserID || message.id, status: 'completed', pending: false };
                            return { ...message, id: serverAssistantID || message.id, pending: false };
                        }));
                    } else if (type === 'tool_status') {
                        // Transient only — lives on the local assistant message,
                        // never sent to or stored in the DB.
                        updateAssistant({ activity: data.tool || null });
                    } else if (type === 'text_delta') {
                        setTransientMessages((current) => current.map((message) => message.role === 'assistant' ? { ...message, content: `${message.content || ''}${data.delta || ''}` } : message));
                    } else if (type === 'person_choices') {
                        updateAssistant({ personChoices: data.choices || [] });
                    } else if (type === 'trusted_sources') {
                        updateAssistant({ trustedSources: data.sources || [] });
                    } else if (type === 'usage') {
                        updateAssistant({ usage: data.usage });
                    } else if (type === 'error') {
                        streamError = data;
                        updateAssistant({ status: 'failed', error: data.message });
                    } else if (type === 'done' && !streamError) {
                        updateAssistant({ status: data.status || 'completed' });
                    }
                },
            });
        } catch (error) {
            const stopped = controller.signal.aborted || error?.name === 'AbortError';
            streamError = stopped ? { message: 'Response stopped.', retryable: true } : error;
            updateAssistant({ status: stopped ? 'interrupted' : 'failed', error: streamError?.message });
        } finally {
            setIsStreaming(false);
            abortRef.current = null;
            if (activeSessionID && acknowledged) {
                await new Promise((resolve) => setTimeout(resolve, 250));
                try {
                    const refreshed = await refresh(activeSessionID);
                    const hasPersistedTerminalResponse = refreshed?.some((message) => message.id === serverAssistantID && ['completed', 'interrupted', 'failed'].includes(message.status));
                    if (hasPersistedTerminalResponse) setTransientMessages([]);
                } catch {
                    // Keep local partial response visible. Query retry can reconcile it later.
                }
            }
        }
    }, [composer, hasPermission, isStreaming, refresh, sessionID]);

    const deleteMutation = useMutation({
        mutationFn: deleteAssistantSession,
        onSuccess: async (deletedID) => {
            queryClient.removeQueries({ queryKey: QueryKeys.assistant.messages(organizationID, personID, deletedID) });
            if (deletedID === sessionID) {
                lastSessionByContext.delete(sessionContextKey(organizationID, personID));
                setSessionID(null);
                setTransientMessages([]);
                setComposer('');
            }
            await queryClient.invalidateQueries({ queryKey: QueryKeys.assistant.sessions(organizationID, personID) });
        },
        onError: (error) => Toast.show({ type: 'error', text2: error.message || 'Could not delete session.' }),
    });

    const revertMutation = useMutation({
        mutationFn: revertAssistantFromMessage,
        onSuccess: async (_data, variables) => {
            setTransientMessages([]);
            setComposer(variables.content);
            await refresh(variables.sessionID);
        },
        onError: (error) => Toast.show({ type: 'error', text2: error.message || 'Could not revert messages.' }),
    });

    const handleRevert = (message) => showAlert({
        title: 'Revert to this prompt?',
        subtitle: 'This prompt and every message after it will be permanently removed.',
        confirmText: 'Revert',
        isDestructive: true,
        onConfirm: () => revertMutation.mutate({ sessionID, userMessageID: message.id, content: message.content }),
    });

    const handleDelete = (session) => showAlert({
        title: 'Delete this session?',
        subtitle: 'The session and its messages will be permanently deleted.',
        confirmText: 'Delete',
        isDestructive: true,
        onConfirm: () => deleteMutation.mutate({ organizationID, sessionID: session.id }),
    });

    const handleRetry = async (assistantMessage) => {
        const index = messages.findIndex((message) => message.id === assistantMessage.id);
        const prompt = messages.slice(0, index).reverse().find((message) => message.role === 'user');
        if (!prompt || isRetrying) return;

        setIsRetrying(true);
        try {
            if (sessionID && !prompt.pending) {
                await revertAssistantFromMessage({ sessionID, userMessageID: prompt.id });
                setTransientMessages([]);
                await refresh(sessionID);
            }
            await send(prompt.content);
        } catch (error) {
            Toast.show({ type: 'error', text2: error.message || 'Could not retry response.' });
        } finally {
            setIsRetrying(false);
        }
    };

    const selectSession = (id) => {
        if (busy) return;
        setTransientMessages([]);
        setComposer('');
        setSessionID(id);
        setSessionsOpen(false);
    };

    const startFresh = () => {
        if (isStreaming) return;
        lastSessionByContext.delete(sessionContextKey(organizationID, personID));
        setSessionID(null);
        setTransientMessages([]);
        setComposer('');
    };

    // Trusted sources: people open the profile sheet; docs open the
    // knowledge base in a new tab (web) or the in-app webview (native).
    const handleOpenSource = useCallback((source) => {
        if (source?.type === 'doc') {
            if (!source.route) return;
            if (isWeb) {
                window.open(source.route, '_blank', 'noopener,noreferrer');
            } else {
                const tab = segments[0] === '(tabs)' ? segments[1] : '(home)';
                router.push({ pathname: `/(tabs)/${tab}/(utilities)/webview`, params: { url: source.route, title: source.label || 'Documentation' } });
            }
            return;
        }
        setProfileModal({ personID: source.recordID, displayName: source.label, allowSend: false });
    }, [isWeb, router, segments]);

    const panelStyle = isWeb ? styles.webPanel(width) : styles.nativePanel;
    const busy = isStreaming || isRetrying || revertMutation.isPending || deleteMutation.isPending;

    if (isClosing) return null;

    return (
        <Animated.View
            testID="assistant-screen"
            ref={isWeb ? registerPortalHost : undefined}
            entering={isWeb ? FadeIn.duration(300) : undefined}
            exiting={isWeb ? FadeOut.duration(300) : undefined}
            style={styles.modalOuter(isWeb, themeName)}
        >
            {isWeb && <Pressable testID="assistant-backdrop" style={StyleSheet.absoluteFill} accessibilityRole="button" accessibilityLabel="Close Assistant" onPress={close} />}
            <Animated.View
                ref={isWeb ? registerModalNode : undefined}
                entering={isWeb ? SlideInRight.duration(300) : undefined}
                exiting={isWeb ? SlideOutRight.duration(300) : undefined}
                style={panelStyle}
            >
                <ModalPortalContext.Provider value={portalHost}>
                <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
                    <View style={styles.header(isWeb)}>
                        <View style={styles.headerIdentity}>
                            <View style={styles.eyebrowRow}>
                                <View style={styles.headerTick} />
                                <Text style={styles.eyebrow}>REACH</Text>
                            </View>
                            <View style={styles.titleRow}>
                                <Text style={styles.title}>Assistant</Text>
                                <View style={styles.alphaBadge}><Text style={styles.alphaText}>ALPHA</Text></View>
                            </View>
                        </View>
                        <View style={styles.headerActions}>
                            <SolidButton
                                testID="assistant-new-session"
                                accessibilityLabel="New session"
                                type="tertiary"
                                iconName="add-outline"
                                iconBefore
                                text={width > 560 ? 'New' : ''}
                                disabled={busy}
                                onPress={startFresh}
                            />
                            <SolidButton
                                testID="assistant-sessions-button"
                                accessibilityLabel="Past sessions"
                                type="tertiary"
                                variant="muted"
                                iconName="time-outline"
                                iconBefore
                                text={width > 560 ? 'Sessions' : ''}
                                onPress={() => setSessionsOpen(true)}
                            />
                            <Pressable testID="assistant-close" accessibilityRole="button" accessibilityLabel="Close Assistant" style={styles.closeButton} onPress={close}>
                                <Unicon name="close" size={22} colorVariant="text" />
                            </Pressable>
                        </View>
                    </View>

                    {userLoading ? (
                        <View style={styles.center}><ActivityIndicator /></View>
                    ) : !hasPermission ? (
                        <View testID="assistant-permission-denied" style={styles.center}>
                            <Unicon name="lock-closed-outline" size={30} colorVariant="textTertiary" />
                            <Text selectable style={styles.permissionTitle}>Assistant is not available</Text>
                            <Text selectable style={styles.permissionText}>You need the Use AI Assistant permission.</Text>
                        </View>
                    ) : (
                        <>
                            <ScrollView ref={scrollRef} style={styles.messages} contentContainerStyle={styles.messagesContent} keyboardShouldPersistTaps="handled" contentInsetAdjustmentBehavior="automatic">
                                {!sessionID && !messages.length && (
                                    <View testID="assistant-empty-state" style={styles.welcome}>
                                        <View style={styles.welcomeMark}><Unicon name="sparkles-outline" size={24} colorVariant="tertiary" /></View>
                                        <Text selectable style={styles.welcomeTitle}>What can I help you find?</Text>
                                        <Text selectable style={styles.welcomeText}>Ask about people, check permissions (“Can I edit events?”), or get help with how to do something in Reach. This feature is in Alpha state and will likely have issues. Please feel free to try it out and give feedback!</Text>
                                        <View testID="assistant-recent-sessions" style={styles.welcomeSection}>
                                            <View style={styles.sectionHeader}>
                                                <Text style={styles.sectionTitle}>Recent Sessions</Text>
                                                <Pressable testID="assistant-view-all-sessions" accessibilityRole="button" accessibilityLabel="View all sessions" style={styles.viewAllButton} onPress={() => setSessionsOpen(true)}>
                                                    <Text style={styles.viewAllText}>View all</Text>
                                                    <Unicon name="arrow-forward" size={14} colorVariant="tertiary" />
                                                </Pressable>
                                            </View>
                                            {sessionsLoading ? (
                                                <ActivityIndicator testID="assistant-recent-sessions-loading" style={styles.recentSessionsStatus} />
                                            ) : sessionsError ? (
                                                <Text testID="assistant-recent-sessions-error" selectable style={styles.recentSessionsStatus}>Could not load recent sessions. Try again later.</Text>
                                            ) : !sessions?.length ? (
                                                <Text selectable style={styles.recentSessionsStatus}>Your sessions will appear here after your first message.</Text>
                                            ) : (
                                                <View style={styles.recentSessionsGrid}>
                                                    {sessions.slice(0, 4).map((session) => (
                                                        <SessionCard
                                                            key={session.id}
                                                            testID={`assistant-recent-session-${session.id}`}
                                                            session={session}
                                                            busy={busy}
                                                            onSelect={selectSession}
                                                            style={styles.recentSessionCard(width > 600)}
                                                        />
                                                    ))}
                                                </View>
                                            )}
                                        </View>
                                        <View style={styles.examples}>
                                            <Text style={styles.sectionTitle}>Try asking</Text>
                                            {EXAMPLES.map((example, index) => (
                                                <Pressable key={example} testID={`assistant-example-${index}`} accessibilityRole="button" accessibilityLabel={`Ask: ${example}`} disabled={busy} style={styles.exampleCard} onPress={() => send(example)}>
                                                    <Text style={styles.exampleText}>{example}</Text>
                                                    <Unicon name="arrow-forward" size={16} colorVariant="tertiary" />
                                                </Pressable>
                                            ))}
                                        </View>
                                    </View>
                                )}
                                {messagesFetching && sessionID && !messages.length && <ActivityIndicator style={styles.loadingMessages} />}
                                {messages.map((message, index) => (
                                    <MessageBubble
                                        key={message.clientKey || message.id || index}
                                        message={message}
                                        streaming={isStreaming && index === messages.length - 1 && message.role === 'assistant'}
                                        disabled={busy}
                                        onRevert={handleRevert}
                                        onRetry={handleRetry}
                                        onPersonSelect={(choice) => setProfileModal({ personID: choice.personID, displayName: choice.displayName, allowSend: true })}
                                        onOpenSource={handleOpenSource}
                                    />
                                ))}
                            </ScrollView>
                            <View style={styles.composerArea}>
                                <View style={styles.composer(busy)}>
                                    <TextInput
                                        testID="assistant-message-input"
                                        accessibilityLabel="Message Assistant"
                                        style={styles.input}
                                        value={composer}
                                        onChangeText={setComposer}
                                        placeholder="Ask Assistant…"
                                        placeholderTextColor={styles.inputPlaceholder.color}
                                        multiline
                                        editable={!busy}
                                        maxLength={4000}
                                        onSubmitEditing={(e) => {
                                            if (isStreaming || busy) return;
                                            if (isWeb) {
                                                // Web: Enter sends, Cmd/Ctrl+Enter inserts newline.
                                                if (e.nativeEvent.keyModifiers?.meta || e.nativeEvent.keyModifiers?.ctrl) {
                                                    setComposer((prev) => `${prev}\n`);
                                                    return;
                                                }
                                                e.preventDefault();
                                                send();
                                            }
                                            // Native: default behavior (return = newline), explicit send button required.
                                        }}
                                        blurOnSubmit={isWeb}
                                        keyboardType={isWeb ? 'default' : 'default'}
                                    />
                                    <Pressable
                                        testID={isStreaming ? 'assistant-stop-button' : 'assistant-send-button'}
                                        accessibilityRole="button"
                                        accessibilityLabel={isStreaming ? 'Stop response' : 'Send message'}
                                        disabled={!isStreaming && (busy || !composer.trim())}
                                        style={styles.sendButton(isStreaming, !isStreaming && (busy || !composer.trim()))}
                                        onPress={() => isStreaming ? abortRef.current?.abort() : send()}
                                    >
                                        <Unicon name={isStreaming ? 'stop' : 'arrow-up'} size={19} colorVariant="onTertiary" />
                                    </Pressable>
                                </View>
                                <Text selectable style={styles.disclaimer}>Assistant can make mistakes. Check linked records before acting.</Text>
                            </View>
                        </>
                    )}
                </KeyboardAvoidingView>
                </ModalPortalContext.Provider>
            </Animated.View>
            {isWeb && <ConfirmationModal />}
            <SessionDrawer
                isOpen={sessionsOpen}
                onClose={() => setSessionsOpen(false)}
                sessions={sessions}
                selectedSessionID={sessionID}
                loading={sessionsLoading}
                error={sessionsError}
                busy={busy}
                onSelect={selectSession}
                onDelete={handleDelete}
            />
            <PersonProfileModal
                testID="assistant-person-profile-modal"
                personID={profileModal?.personID}
                onClose={() => setProfileModal(null)}
                title="Profile"
                subtitle={profileModal?.displayName}
                confirmText={profileModal?.allowSend ? 'Send to Assistant' : undefined}
                onConfirm={profileModal?.allowSend ? () => {
                    const selection = { type: 'person', personID: profileModal.personID, displayName: profileModal.displayName };
                    setProfileModal(null);
                    send({ selection });
                } : undefined}
            />
        </Animated.View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness, shadows }, rt) => ({
    modalOuter: (isWeb, themeName) => ({ flex: 1, flexDirection: isWeb ? 'row' : 'column', alignItems: isWeb ? 'stretch' : 'stretch', justifyContent: isWeb ? 'flex-end' : 'flex-start', backgroundColor: isWeb ? (themeName === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.3)') : colors.background, cursor: isWeb ? 'pointer' : 'default' }),
    webPanel: (width) => ({ width: Math.min(920, width), maxWidth: '100%', height: '100%', backgroundColor: colors.background, overflow: 'hidden', boxShadow: shadows.elevated, cursor: 'default' }),
    nativePanel: { flex: 1, width: '100%', backgroundColor: colors.background },
    header: (isWeb) => ({ minHeight: 88 + (isWeb ? 0 : rt.insets.top), paddingTop: spacing.md + (isWeb ? 0 : rt.insets.top), paddingBottom: spacing.md, paddingHorizontal: spacing.md, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, backgroundColor: colors.background }),
    headerIdentity: { flexShrink: 1, gap: spacing.xs },
    eyebrowRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    headerTick: { width: 16, height: 3, backgroundColor: colors.secondary, transform: [{ skewX: '-35deg' }] },
    eyebrow: { color: colors.tertiary, fontFamily: fonts.bold, fontSize: typography.micro, letterSpacing: 1.6 },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    title: { color: colors.text, fontFamily: fonts.bold, fontSize: typography.h1, letterSpacing: -0.6 },
    alphaBadge: { paddingHorizontal: spacing.xs + 2, paddingVertical: 3, borderRadius: 3, backgroundColor: colors.tertiaryContainer },
    alphaText: { color: colors.tertiary, fontFamily: fonts.bold, fontSize: typography.micro, letterSpacing: 0.8 },
    headerActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    closeButton: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 3, _web: { cursor: 'pointer', _hover: { backgroundColor: colors.hover } } },
    center: { flex: 1, padding: spacing.xl, alignItems: 'center', justifyContent: 'center', gap: spacing.sm },
    permissionTitle: { color: colors.text, fontFamily: fonts.heading, fontSize: typography.h2 },
    permissionText: { color: colors.textSecondary, fontFamily: fonts.body, fontSize: typography.body, textAlign: 'center' },
    messages: { flex: 1 },
    messagesContent: { width: '100%', maxWidth: 760, alignSelf: 'center', paddingHorizontal: spacing.md, paddingTop: spacing.xl, paddingBottom: spacing.lg, gap: spacing.lg },
    welcome: { flex: 1, width: '100%', minHeight: 440, alignItems: 'center', justifyContent: 'center', paddingVertical: spacing.lg, gap: spacing.sm },
    welcomeMark: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.tertiaryContainer },
    welcomeTitle: { marginTop: spacing.sm, color: colors.text, fontFamily: fonts.heading, fontSize: typography.hero, textAlign: 'center' },
    welcomeText: { maxWidth: 520, color: colors.textSecondary, fontFamily: fonts.body, fontSize: typography.body, lineHeight: 22, textAlign: 'center' },
    welcomeSection: { width: '100%', maxWidth: 600, marginTop: spacing.lg, gap: spacing.sm },
    sectionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
    sectionTitle: { color: colors.onSurfaceVariant, fontFamily: fonts.bold, fontSize: typography.caption },
    viewAllButton: { minHeight: 36, paddingHorizontal: spacing.xs, flexDirection: 'row', alignItems: 'center', gap: spacing.xs, _web: { cursor: 'pointer', _hover: { opacity: 0.72 } } },
    viewAllText: { color: colors.tertiary, fontFamily: fonts.body, fontSize: typography.caption },
    recentSessionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    recentSessionCard: (twoColumns) => ({ width: twoColumns ? '48%' : '100%', flexGrow: 1 }),
    recentSessionsStatus: { paddingVertical: spacing.sm, color: colors.textSecondary, fontFamily: fonts.body, fontSize: typography.caption, lineHeight: 20 },
    examples: { width: '100%', maxWidth: 600, marginTop: spacing.lg, gap: spacing.sm },
    exampleCard: { minHeight: 52, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, borderRadius: roundness.md, backgroundColor: colors.surface, boxShadow: shadows.card, _web: { cursor: 'pointer', _hover: { backgroundColor: colors.hoverCard, boxShadow: shadows.cardHover } } },
    exampleText: { flex: 1, color: colors.text, fontFamily: fonts.body, fontSize: typography.body },
    loadingMessages: { padding: spacing.xl },
    messageRow: (isUser) => ({ width: '100%', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'stretch', gap: spacing.sm }),
    messageColumn: (isUser) => ({ maxWidth: '84%', alignItems: isUser ? 'flex-end' : 'flex-start', gap: spacing.xs }),
    bubble: (isUser) => ({ paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2, borderRadius: roundness.lg, borderBottomRightRadius: isUser ? 5 : roundness.lg, borderBottomLeftRadius: isUser ? roundness.lg : 5, backgroundColor: isUser ? colors.tertiary : colors.surface, boxShadow: isUser ? shadows.none : shadows.card, flexDirection: 'row', alignItems: 'flex-end', gap: 3 }),
    messageText: (isUser) => ({ color: isUser ? colors.onTertiary : colors.text, fontFamily: fonts.body, fontSize: typography.body, lineHeight: 22 }),
    cursor: { width: 2, height: 16, marginBottom: 3, backgroundColor: colors.tertiary },
    statusLine: { color: colors.textTertiary, fontFamily: fonts.body, fontSize: typography.body, fontStyle: 'italic', lineHeight: 22 },
    messageActions: (isUser) => ({ minHeight: 18, paddingHorizontal: spacing.xs, flexDirection: isUser ? 'row-reverse' : 'row', gap: spacing.sm, alignItems: 'center' }),
    actionText: (disabled) => ({ color: disabled ? colors.textTertiary : colors.tertiary, fontFamily: fonts.body, fontSize: typography.micro }),
    statusText: { color: colors.error, fontFamily: fonts.body, fontSize: typography.micro },
    choiceList: { width: '100%', minWidth: 260, paddingTop: spacing.xs, gap: spacing.xs },
    choiceLabel: { color: colors.textTertiary, fontFamily: fonts.bold, fontSize: typography.micro, letterSpacing: 0.5, textTransform: 'uppercase' },
    choiceCard: (disabled) => ({ minHeight: 54, padding: spacing.sm, flexDirection: 'row', alignItems: 'center', gap: spacing.sm, borderRadius: roundness.md, backgroundColor: colors.surface, boxShadow: shadows.card, opacity: disabled ? 0.55 : 1, _web: { cursor: disabled ? 'default' : 'pointer', _hover: disabled ? {} : { backgroundColor: colors.hoverCard } } }),
    choiceText: { flex: 1 },
    choiceName: { color: colors.text, fontFamily: fonts.bold, fontSize: typography.body },
    choiceDisambiguator: { color: colors.textSecondary, fontFamily: fonts.body, fontSize: typography.caption },
    sources: { maxWidth: '100%', flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
    sourceChip: { maxWidth: 220, minHeight: 28, paddingHorizontal: spacing.sm, flexDirection: 'row', alignItems: 'center', gap: spacing.xs, borderRadius: 14, backgroundColor: colors.tertiaryContainer, _web: { cursor: 'pointer', _hover: { backgroundColor: colors.tertiaryContainer } } },
    sourceText: { flexShrink: 1, color: colors.tertiary, fontFamily: fonts.body, fontSize: typography.caption },
    composerArea: { paddingHorizontal: spacing.md, paddingTop: spacing.sm, paddingBottom: Platform.OS === 'web' ? spacing.md : Math.max(spacing.md, rt.insets.bottom), alignItems: 'center', backgroundColor: colors.background },
    composer: () => ({ width: '100%', maxWidth: 760, minHeight: 54, maxHeight: 150, paddingLeft: spacing.md, paddingRight: spacing.xs, paddingVertical: spacing.xs, flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm, borderRadius: roundness.lg, backgroundColor: colors.surface, boxShadow: shadows.card }),
    input: { flex: 1, minHeight: 44, maxHeight: 130, paddingVertical: 11, color: colors.text, fontFamily: fonts.body, fontSize: typography.body, _web: { outlineStyle: 'none' } },
    inputPlaceholder: { color: colors.textTertiary },
    sendButton: (stopping, disabled) => ({ width: 35, height: 35, marginBottom:5, marginRight:5, borderRadius: 21, alignItems: 'center', justifyContent: 'center', backgroundColor: stopping ? colors.error : colors.tertiary, opacity: disabled ? 0.4 : 1, _web: { cursor: disabled ? 'default' : 'pointer' } }),
    disclaimer: { paddingTop: spacing.xs, color: colors.textTertiary, fontFamily: fonts.body, fontSize: typography.micro, textAlign: 'center' },
    sessionList: { padding: spacing.md, paddingBottom: spacing.xl, gap: spacing.sm, backgroundColor: colors.background },
    emptySessions: { padding: spacing.lg, color: colors.textSecondary, fontFamily: fonts.body, fontSize: typography.body, textAlign: 'center' },
    sessionCard: (selected) => ({ backgroundColor: selected ? colors.tertiaryContainer : colors.surface }),
    sessionRow: { minHeight: 88, flexDirection: 'row', alignItems: 'stretch' },
    sessionSelect: { flex: 1, padding: spacing.md, paddingRight: spacing.lg, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    sessionIcon: (selected) => ({ width: 34, height: 34, borderRadius: 3, backgroundColor: selected ? colors.tertiaryContainer : colors.background, alignItems: 'center', justifyContent: 'center' }),
    sessionDetails: { flex: 1, gap: spacing.xs },
    sessionRowTitle: { color: colors.text, fontFamily: fonts.heading, fontSize: typography.caption, lineHeight: 19 },
    sessionDate: { color: colors.textTertiary, fontFamily: fonts.body, fontSize: typography.micro, fontVariant: ['tabular-nums'] },
    deleteButton: { width: 40, marginRight: spacing.sm, alignItems: 'center', justifyContent: 'center', _web: { cursor: 'pointer', _hover: { backgroundColor: colors.errorContainer } } },
}));

export default AssistantScreen;
